import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle, ArrowRight, Coin, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";

const eur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export default function Abschluss() {
  const [data, setData] = useState({ abgeschlossen: [], bereit_zum_abschluss: [], summe_zahlung: 0, anzahl_abgeschlossen: 0 });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ zahlung_betrag: "", zahlung_eingegangen_am: "", notizen: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/abschluss");
      setData(r.data);
    } catch {
      toast.error("Konnte Abschluss-Daten nicht laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openClose = (a) => {
    setDialog(a);
    setForm({ zahlung_betrag: a.bezifferung?.schadensumme || "", zahlung_eingegangen_am: new Date().toISOString().slice(0, 10), notizen: "" });
  };

  const closeAkte = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        zahlung_betrag: form.zahlung_betrag ? Number(form.zahlung_betrag) : null,
        zahlung_eingegangen_am: form.zahlung_eingegangen_am || null,
        notizen: form.notizen || null,
      };
      await api.post(`/akten/${dialog.id}/abschluss`, payload);
      toast.success(`Akte ${dialog.aktenzeichen} abgeschlossen`);
      setDialog(null);
      load();
    } catch {
      toast.error("Fehler beim Abschließen");
    }
  };

  return (
    <div className="space-y-8" data-testid="abschluss-page">
      <header>
        <p className="overline text-[#D32F2F] mb-2">Workflow · 9</p>
        <h1 className="font-serif text-3xl lg:text-4xl">Abschluss</h1>
        <p className="text-sm text-[#6B7280] mt-2 max-w-2xl">
          Zahlung eingegangen? Akte abschließen. Hier sehen Sie alle erledigten Akten und die kumulierte Realisierungssumme.
        </p>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 flex items-center gap-4" data-testid="abschluss-kpi-anzahl">
          <Trophy size={32} weight="duotone" className="text-[#1A2238]" />
          <div>
            <p className="overline text-[#9CA3AF]">Abgeschlossene Akten</p>
            <p className="text-3xl font-serif">{data.anzahl_abgeschlossen}</p>
          </div>
        </div>
        <div className="bg-[#1A2238] text-white rounded-lg p-6 flex items-center gap-4" data-testid="abschluss-kpi-summe">
          <Coin size={32} weight="duotone" />
          <div>
            <p className="overline text-white/60">Realisierte Zahlungen</p>
            <p className="text-3xl font-serif">{eur(data.summe_zahlung)}</p>
          </div>
        </div>
      </div>

      {/* Bereit zum Abschluss */}
      <section data-testid="abschluss-bereit-section">
        <h2 className="font-serif text-2xl mb-4">Bereit zum Abschluss <span className="text-[#9CA3AF] text-base font-sans">({data.bereit_zum_abschluss.length})</span></h2>
        {loading ? (
          <p className="text-sm text-[#9CA3AF]">Lade …</p>
        ) : data.bereit_zum_abschluss.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">Keine Akten bereit zum Abschluss.</p>
        ) : (
          <ul className="space-y-2">
            {data.bereit_zum_abschluss.map(a => (
              <li key={a.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex flex-wrap items-center gap-4" data-testid={`abschluss-bereit-${a.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-medium">{a.aktenzeichen} · {a.mandant_name || "—"}</p>
                  <p className="text-xs text-[#9CA3AF]">Status: {a.status} · Schadensumme: {eur(a.bezifferung?.schadensumme || 0)}</p>
                </div>
                <Button asChild variant="outline" size="sm">
                  <Link to={`/app/akten/${a.id}`}>Öffnen <ArrowRight size={14} className="ml-1" /></Link>
                </Button>
                <Button onClick={() => openClose(a)} className="bg-[#16A34A] hover:bg-[#15803D]" size="sm" data-testid={`abschluss-close-${a.id}`}>
                  <CheckCircle size={14} className="mr-1" /> Akte schließen
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Abgeschlossen */}
      <section data-testid="abschluss-erledigt-section">
        <h2 className="font-serif text-2xl mb-4">Abgeschlossene Akten <span className="text-[#9CA3AF] text-base font-sans">({data.abgeschlossen.length})</span></h2>
        {data.abgeschlossen.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">Noch keine Akte abgeschlossen.</p>
        ) : (
          <ul className="space-y-2">
            {data.abgeschlossen.map(a => (
              <li key={a.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex flex-wrap items-center gap-4 opacity-80" data-testid={`abschluss-row-${a.id}`}>
                <CheckCircle size={20} weight="fill" className="text-[#16A34A] shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <p className="font-medium">{a.aktenzeichen} · {a.mandant_name || "—"}</p>
                  <p className="text-xs text-[#9CA3AF]">
                    Abgeschlossen: {a.abgeschlossen_am ? new Date(a.abgeschlossen_am).toLocaleDateString("de-DE") : "—"}
                    {a.zahlung_betrag != null && <> · Zahlung: {eur(a.zahlung_betrag)}</>}
                  </p>
                </div>
                <Button asChild variant="ghost" size="sm">
                  <Link to={`/app/akten/${a.id}`}>Öffnen</Link>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Akte abschließen · {dialog?.aktenzeichen}</DialogTitle>
          </DialogHeader>
          <form onSubmit={closeAkte} className="space-y-4">
            <div>
              <Label>Zahlungsbetrag (EUR)</Label>
              <Input type="number" step="0.01" value={form.zahlung_betrag} onChange={(e) => setForm({ ...form, zahlung_betrag: e.target.value })} placeholder="0,00" data-testid="abschluss-betrag" />
            </div>
            <div>
              <Label>Zahlung eingegangen am</Label>
              <Input type="date" value={form.zahlung_eingegangen_am} onChange={(e) => setForm({ ...form, zahlung_eingegangen_am: e.target.value })} data-testid="abschluss-datum" />
            </div>
            <div>
              <Label>Notiz (optional)</Label>
              <Textarea rows={3} value={form.notizen} onChange={(e) => setForm({ ...form, notizen: e.target.value })} placeholder="Bemerkung zum Abschluss" data-testid="abschluss-notiz" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)}>Abbrechen</Button>
              <Button type="submit" className="bg-[#16A34A] hover:bg-[#15803D]" data-testid="abschluss-submit">
                <CheckCircle size={14} className="mr-1" /> Abschließen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
